import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Space, Result, DatePicker, Alert } from "antd";
import dayjs from "dayjs";
import { useStepsForm } from "sunflower-antd";
import {
  disabledInceptionMonth,
  activateInceptionDateFormRule,
} from "../../../utils/systemValidation";

interface IModalProps {
  show: boolean;
  showModal: Function;
  handleActivate?: Function;
  memberId: any;
  currentInceptionDate?: string | null;
}

const ActivatePolicyModal: React.FC<IModalProps> = ({
  show,
  showModal,
  handleActivate,
  memberId,
  currentInceptionDate,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const handleClose = () => {
    showModal();
    gotoStep(0);
    form.resetFields();
    setErrorMsg("");
  };

  const { form, current, gotoStep, formProps, submit } = useStepsForm({
    isBackValidate: false,
    async submit(values: any) {
      setLoading(true);
      setErrorMsg("");
      if (handleActivate == undefined) {
        setLoading(false);
        return false;
      }

      const payload = {
        InceptionDate: dayjs(values.InceptionDate)
          .startOf("month")
          .format("YYYY-MM-DD"),
      };

      try {
        const response = await handleActivate(memberId, payload);
        setLoading(false);
        if (response?.success === false) {
          setErrorMsg(response?.message || "Failed to activate policy.");
          return false;
        }
        return true;
      } catch (err: any) {
        setLoading(false);
        setErrorMsg(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to activate policy."
        );
        return false;
      }
    },
    total: 3,
  });

  useEffect(() => {
    if (show) {
      form.setFieldsValue({
        InceptionDate: dayjs().startOf("month").add(2, "hour"),
      });
    }
  }, [show, form]);

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 8 },
  };

  const tailLayout = {
    labelCol: { span: 8 },
    wrapperCol: { offset: 8, span: 8 },
  };

  const display: any = {
    0: (
      <>
        {currentInceptionDate && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Current inception date: ${currentInceptionDate}`}
            description="Choose a new inception month. Allowed: previous month, current month, or any future month (saved as the 1st)."
          />
        )}
        <Form.Item
          name="InceptionDate"
          label="Inception Date"
          help="Previous month, current month, or any future month."
          rules={[{ required: true }, activateInceptionDateFormRule]}
        >
          <DatePicker
            picker="month"
            format="MMMM YYYY"
            placeholder="Inception Date"
            size="large"
            style={{ width: "100%" }}
            disabledDate={disabledInceptionMonth}
            onChange={(val) => {
              if (val) {
                form.setFieldsValue({
                  InceptionDate: val.startOf("month").add(2, "hour"),
                });
              }
            }}
          />
        </Form.Item>

        <Form.Item {...tailLayout}>
          <Space direction="vertical" style={{ width: "100%" }}>
            <Button
              loading={loading}
              type="primary"
              style={{ marginBottom: 10 }}
              block
              size="large"
              onClick={() => {
                submit().then((result: any) => {
                  if (result) {
                    gotoStep(current + 1);
                  } else if (!result) {
                    gotoStep(current + 2);
                  }
                });
              }}
            >
              Activate Policy
            </Button>
            <Button block size="large" onClick={handleClose}>
              Cancel
            </Button>
          </Space>
        </Form.Item>
      </>
    ),
  };

  return (
    <Modal
      title="Activate Policy"
      open={show}
      onOk={handleClose}
      onCancel={handleClose}
      width={900}
      footer={null}
      destroyOnClose
    >
      <div>
        <div style={{ marginTop: 24 }}>
          <Form {...layout} {...formProps} style={{ maxWidth: 900 }}>
            {display[current]}
          </Form>

          {current === 1 && (
            <Result status="success" title="Policy activated successfully.">
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button block size="large" onClick={handleClose}>
                  Okay
                </Button>
              </Space>
            </Result>
          )}

          {current === 2 && (
            <Result
              status="error"
              title="Could not activate policy."
              subTitle={errorMsg || "Please try again."}
            >
              <Space direction="vertical" style={{ width: "100%" }}>
                <Button
                  block
                  size="large"
                  onClick={() => {
                    setErrorMsg("");
                    gotoStep(0);
                  }}
                >
                  Try Again
                </Button>
                <Button block size="large" onClick={handleClose}>
                  Close
                </Button>
              </Space>
            </Result>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ActivatePolicyModal;
